// pages/NotFound.tsx - Google Adsense Style 404
import React from "react";
import { Link } from "react-router-dom";
import {
  ExclamationTriangleIcon,
  HomeIcon,
  ArrowLeftIcon,
  MagnifyingGlassIcon as SearchIcon,
  QuestionMarkCircleIcon,
} from "@heroicons/react/24/outline";
import logo from "../assets/images/logo.png";

const NotFound: React.FC = () => {
  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      {/* Google-style Top Nav */}
      <header className="h-16 bg-white border-b border-gray-200 px-6 flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <img src={logo} alt="AdSense" className="h-8 w-auto" />
          <div className="h-8 w-px bg-gray-300"></div>
          <span className="text-gray-600 text-sm">Error 404</span>
        </div>

        <div className="flex items-center space-x-4">
          <Link
            to="/help"
            className="text-sm text-gray-600 hover:text-gray-900"
          >
            Help
          </Link>
          <Link
            to="/"
            className="px-4 py-2 bg-[#1a73e8] text-white rounded hover:bg-[#0d62d9] text-sm"
          >
            Go to Home
          </Link>
        </div>
      </header>

      {/* Main Content - Google Style */}
      <main className="flex-1 flex items-center justify-center p-6">
        <div className="max-w-2xl w-full text-center">
          {/* Error Icon */}
          <div className="mb-8 flex justify-center">
            <div className="relative">
              <div className="h-32 w-32 bg-gradient-to-br from-red-100 to-red-50 rounded-full flex items-center justify-center border-8 border-white shadow-lg">
                <ExclamationTriangleIcon className="h-16 w-16 text-red-600" />
              </div>
              <div className="absolute -top-2 -right-2 h-12 w-12 bg-white rounded-full flex items-center justify-center shadow-lg">
                <span className="text-2xl font-bold text-red-600">4</span>
              </div>
              <div className="absolute -bottom-2 -left-2 h-12 w-12 bg-white rounded-full flex items-center justify-center shadow-lg">
                <span className="text-2xl font-bold text-red-600">4</span>
              </div>
            </div>
          </div>

          {/* Error Message */}
          <h1 className="text-4xl lg:text-5xl font-normal text-gray-900 mb-4 tracking-tight">
            Page not found
          </h1>
          <p className="text-lg text-gray-600 mb-8 max-w-lg mx-auto">
            The page you're looking for doesn't exist or has been moved.
          </p>

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-12">
            <Link
              to="/"
              className="inline-flex items-center justify-center px-6 py-3 bg-[#1a73e8] text-white rounded-lg hover:bg-[#0d62d9] transition-colors shadow-sm"
            >
              <HomeIcon className="h-5 w-5 mr-2" />
              Go to Dashboard
            </Link>
            <button
              onClick={() => window.history.back()}
              className="inline-flex items-center justify-center px-6 py-3 border border-gray-300 bg-white text-gray-700 rounded-lg hover:bg-gray-50 transition-colors shadow-sm"
            >
              <ArrowLeftIcon className="h-5 w-5 mr-2" />
              Go Back
            </button>
          </div>

          {/* Helpful Links */}
          <div className="bg-white rounded-lg border border-gray-200 p-6 max-w-lg mx-auto shadow-sm">
            <div className="flex items-center justify-center space-x-2 mb-4">
              <QuestionMarkCircleIcon className="h-5 w-5 text-gray-500" />
              <h3 className="font-medium text-gray-900">What to try next</h3>
            </div>

            <div className="space-y-4">
              {/* Search Bar */}
              <div className="relative">
                <SearchIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search AdSense..."
                  className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:border-[#1a73e8] focus:ring-1 focus:ring-[#1a73e8]"
                  onKeyPress={(e) => {
                    if (e.key === "Enter") {
                      // Implement search functionality
                      console.log("Search:", e.currentTarget.value);
                    }
                  }}
                />
              </div>

              {/* Quick Links */}
              <div className="grid grid-cols-2 gap-3">
                {[
                  { label: "Performance", path: "/performance" },
                  { label: "Ad Units", path: "/ad-units" },
                  { label: "Settings", path: "/settings" },
                  { label: "Help Center", path: "/help" },
                ].map((link) => (
                  <Link
                    key={link.path}
                    to={link.path}
                    className="p-3 border border-gray-200 rounded-lg hover:border-[#1a73e8] hover:bg-[#f8f9fa] transition-colors text-center"
                  >
                    <span className="text-sm font-medium text-gray-900">
                      {link.label}
                    </span>
                  </Link>
                ))}
              </div>
            </div>
          </div>

          {/* Troubleshooting Tips */}
          <div className="mt-8 p-6 bg-blue-50 border border-blue-100 rounded-lg max-w-lg mx-auto">
            <h4 className="font-medium text-gray-900 mb-3">
              Common issues and solutions
            </h4>
            <ul className="text-left space-y-2 text-sm text-gray-600">
              <li className="flex items-start">
                <span className="inline-block h-2 w-2 bg-blue-500 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                <span>Check if the URL is spelled correctly</span>
              </li>
              <li className="flex items-start">
                <span className="inline-block h-2 w-2 bg-blue-500 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                <span>Use the search bar above to find what you need</span>
              </li>
              <li className="flex items-start">
                <span className="inline-block h-2 w-2 bg-blue-500 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                <span>Contact support if you believe this is an error</span>
              </li>
            </ul>
            <Link
              to="/help"
              className="inline-block mt-4 text-sm text-[#1a73e8] hover:text-[#0d62d9] font-medium"
            >
              Visit Help Center →
            </Link>
          </div>

          {/* Error Code Details */}
          <div className="mt-8 p-4 bg-gray-50 rounded-lg inline-block">
            <div className="flex items-center space-x-2 text-sm text-gray-500">
              <span>Error code: 404</span>
              <span className="h-1 w-1 bg-gray-400 rounded-full"></span>
              <span>Page not found</span>
              <span className="h-1 w-1 bg-gray-400 rounded-full"></span>
              <span>HTTP 404</span>
            </div>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="py-6 border-t border-gray-200 bg-white">
        <div className="max-w-2xl mx-auto px-6">
          <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
            <div className="flex items-center space-x-4">
              <div className="flex items-center space-x-2">
                <img src={logo} alt="AdSense" className="h-6 w-auto" />
                <span className="text-gray-700 font-medium">AdSense Clone</span>
              </div>
              <span className="text-gray-400">|</span>
              <span className="text-sm text-gray-500">
                © {new Date().getFullYear()} All rights reserved
              </span>
            </div>

            <div className="flex items-center space-x-6 text-sm">
              <Link to="/" className="text-gray-600 hover:text-gray-900">
                Home
              </Link>
              <Link to="/help" className="text-gray-600 hover:text-gray-900">
                Help
              </Link>
              <Link
                to="/feedback"
                className="text-gray-600 hover:text-gray-900"
              >
                Feedback
              </Link>
              <Link
                to="/settings"
                className="text-gray-600 hover:text-gray-900"
              >
                Settings
              </Link>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default NotFound;
